/**
 * timeBlocks.ts — the callback "preferred time" TIME-BLOCK vocabulary
 * (UX Wave 9 §5).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 9.
 *
 * WHY: every place a preferred call time is captured (the journey/widget
 * callback form, the convert-to-callback dialog, the handover schedule) offers
 * the SAME five blocks — no free text, no datetime picker. The wire value is
 * the block string itself ("08:00-10:00"); clinic-service validates writes
 * against this exact list ([CONTRACTS GAP] — the contracts field stays a
 * string, enum both ends), so this module must stay in lock-step with the
 * server's `src/common/time-blocks.ts`.
 *
 * Legacy posture: values stored BEFORE Wave 9 are free text ("Weekday
 * evenings…") or an ISO instant (the old handover datetime) — render those
 * through {@link formatCallWindow} so history stays readable.
 */
import { formatUtcDate } from './format';

/** The five bookable call windows, wire-exact ("enum both ends"). */
export const TIME_BLOCKS = [
  '08:00-10:00',
  '10:00-12:00',
  '12:00-15:00',
  '15:00-17:00',
  '17:00-19:00',
] as const;

export type TimeBlock = (typeof TIME_BLOCKS)[number];

/** Human labels for the Select options — descriptor + the exact window. */
export const TIME_BLOCK_LABELS: Record<TimeBlock, string> = {
  '08:00-10:00': 'Morning 8:00–10:00',
  '10:00-12:00': 'Late morning 10:00–12:00',
  '12:00-15:00': 'Early afternoon 12:00–15:00',
  '15:00-17:00': 'Late afternoon 15:00–17:00',
  '17:00-19:00': 'Evening 17:00–19:00',
};

/**
 * Render a stored call-window value: a Wave-9 block shows its label; a legacy
 * ISO instant renders as the old date-time; anything else (legacy free text)
 * renders verbatim — "keep legacy stored free-text readable".
 */
export function formatCallWindow(value: string): string {
  if ((TIME_BLOCKS as readonly string[]).includes(value)) {
    return TIME_BLOCK_LABELS[value as TimeBlock];
  }
  // A parseable instant is a pre-Wave-9 handover datetime.
  if (!Number.isNaN(Date.parse(value))) return formatUtcDate(value);
  return value;
}
